"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { useCart } from "@/components/CartProvider";
import { formatPrice } from "@/lib/catalog";

const TEXT = {
  cart: "Корзина",
  empty: "Корзина пока пуста",
  total: "Итого",
  open: "Открыть корзину",
  remove: "Удалить товар",
  minus: "Уменьшить количество",
  plus: "Увеличить количество",
  items: "шт."
} as const;

function CartIcon() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24">
      <path
        d="M4 6h2l1.4 7.2a1 1 0 0 0 1 .8h8.8a1 1 0 0 0 1-.8L19.8 8H7.2"
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.8"
      />
      <circle cx="10" cy="18" fill="currentColor" r="1.2" />
      <circle cx="17" cy="18" fill="currentColor" r="1.2" />
    </svg>
  );
}

export function HeaderCartButton() {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<number | null>(null);
  const { items, totalAmount, totalQty, removeItem, updateQty } = useCart();
  const [isOpen, setIsOpen] = useState(false);

  function clearCloseTimer() {
    if (closeTimerRef.current) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }

  function openPreview() {
    clearCloseTimer();
    setIsOpen(true);
  }

  function scheduleClose() {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setIsOpen(false), 120);
  }

  useEffect(() => {
    function handlePointerDown(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    function handleEscape(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    window.addEventListener("mousedown", handlePointerDown);
    window.addEventListener("keydown", handleEscape);
    return () => {
      clearCloseTimer();
      window.removeEventListener("mousedown", handlePointerDown);
      window.removeEventListener("keydown", handleEscape);
    };
  }, []);

  return (
    <div
      className={`header-cart-shell ${isOpen ? "header-cart-shell--open" : ""}`}
      onMouseEnter={openPreview}
      onMouseLeave={scheduleClose}
      ref={containerRef}
    >
      <Link className="header-action-link cart-header-button" href="/cart" onFocus={openPreview}>
        <span className="header-action-icon">
          <CartIcon />
        </span>
        <span className="header-action-label">{TEXT.cart}</span>
        {totalQty > 0 ? <span className="cart-counter">{totalQty}</span> : null}
      </Link>

      {isOpen ? (
        <div className="header-cart-preview" onMouseEnter={openPreview} onMouseLeave={scheduleClose}>
          <div className="header-cart-preview-head">
            <strong>{TEXT.cart}</strong>
            <span>{totalQty ? `${totalQty} ${TEXT.items}` : TEXT.empty}</span>
          </div>

          {items.length ? (
            <div className="header-cart-preview-items">
              {items.slice(0, 4).map((item) => (
                <div className="header-cart-preview-item" key={item.slug}>
                  <Link
                    className="header-cart-preview-item-link"
                    href={`/product/${item.slug}`}
                    onClick={() => setIsOpen(false)}
                  >
                    <Image
                      alt={item.name}
                      className="header-cart-preview-image"
                      height={56}
                      sizes="56px"
                      src={item.image}
                      width={56}
                    />
                    <div className="header-cart-preview-copy">
                      <strong>{item.name}</strong>
                      <span>{formatPrice(item.price)}</span>
                    </div>
                  </Link>

                  <div className="header-cart-preview-controls">
                    <div className="header-cart-preview-stepper">
                      <button
                        aria-label={`${TEXT.minus}: ${item.name}`}
                        className="header-cart-preview-step"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          if (item.qty <= 1) {
                            removeItem(item.slug);
                            return;
                          }
                          updateQty(item.slug, item.qty - 1);
                        }}
                        type="button"
                      >
                        –
                      </button>
                      <span>{item.qty}</span>
                      <button
                        aria-label={`${TEXT.plus}: ${item.name}`}
                        className="header-cart-preview-step"
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          updateQty(item.slug, item.qty + 1);
                        }}
                        type="button"
                      >
                        +
                      </button>
                    </div>

                    <button
                      aria-label={`${TEXT.remove}: ${item.name}`}
                      className="header-cart-preview-remove"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        removeItem(item.slug);
                      }}
                      type="button"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="header-cart-preview-empty">{TEXT.empty}</div>
          )}

          <div className="header-cart-preview-foot">
            <div className="header-cart-preview-total">
              <span>{TEXT.total}</span>
              <strong>{formatPrice(totalAmount)}</strong>
            </div>
            <Link className="header-cart-preview-cta" href="/cart" onClick={() => setIsOpen(false)}>
              {TEXT.open}
            </Link>
          </div>
        </div>
      ) : null}
    </div>
  );
}
